import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import {
  patchMasterBlockFloorPermissions,
  transformUserPermissionArr,
} from "../components/common/CommonFunctions";
import { clearActiveBranch, enforceOrgAdminBranchPolicy } from "../utils/branchSession";
import { refreshAuthSession } from "../utils/sessionRefresh";

export const AuthContext = createContext("");

export const SessionProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authRevision, setAuthRevision] = useState(0);

  const bumpAuthRevision = useCallback(() => {
    setAuthRevision((prev) => prev + 1);
  }, []);

  // Define all keys used in storage
  const storageKeys = [
    "token",
    "is_super_admin",
    "is_admin",
    "is_system_admin",
    "userId",
    "user_group_id",
    "address",
    "companyName",
    "profile",
    "employee_id",
    "employee_name",
    "designation",
    "department",
    "department_id",
    "loginUserPermission",
    "employeeProfileAccess",
    "modules",
    "subModule",
    "planName",
    "is_password_updated",
    "passwordPolicy",
    "shiftDetails",
    "customConfig",
    "salary_calculation",
    // Branch management: role + delegated permissions + branch of the logged-in
    // user, and branch_avail (org admin + at least one branch). Drive UI gating
    // via useBranchRole / login routing.
    "is_branch_admin",
    // Per-employee training opt-in; org-level NABH stays in customConfig.
    "is_training_enabled",
    "can_create",
    "can_edit",
    "can_delete",
    "branch_id",
    "branch_name",
    "branch_avail",
    "multi_branch",
    "org_admin_branch_selection",
  ];

  /** --------------------------
   *  Helpers
   *  ------------------------- */
  const setStorage = (storage, data) => {
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        storage.setItem(key, typeof value === "object" ? JSON.stringify(value) : value);
      }
    });
  };

  const removeStorage = (storage) => {
    storageKeys.forEach((key) => storage.removeItem(key));
  };

  const parsePermissions = (loginData) => {
    return loginData?.permissions?.permissions?.length
      ? patchMasterBlockFloorPermissions(
          transformUserPermissionArr([...loginData.permissions.permissions])
        )
      : [];
  };

  /** --------------------------
   *  Login
   *  ------------------------- */
  const loginauth = useCallback(async (loginData, keepSignedIn) => {
    try {
      const dataList = parsePermissions(loginData);
      const moduleList = loginData?.modules ? [...loginData.modules] : [];
      const subModuleList = loginData?.sub_modules ? [...loginData.sub_modules] : [];

      const commonData = {
        token: loginData.token,
        is_super_admin: loginData.is_super_admin,
        is_admin: loginData.is_admin,
        is_system_admin: loginData.is_system_admin,
        userId: loginData.user_id,
        user_group_id: loginData.user_group_id,
        address: loginData.address,
        companyName: loginData.company_name,
        profile: loginData.profile,
        employee_id: loginData.employee_id,
        employee_name: loginData.employee_name,
        designation: loginData.designation,
        department: loginData.department,
        department_id: loginData.department_id ?? null,
        loginUserPermission: dataList,
        employeeProfileAccess: loginData?.permissions?.profile_access || {},
        modules: moduleList,
        subModule: subModuleList,
        planName: loginData.plan_name,
        is_password_updated: loginData.is_password_updated,
        reportingManager: loginData.is_reproting_manager,
        // Branch management. Defaults keep old tokens working: no manager role,
        // read-only, no branch, branch flow off.
        is_branch_admin: loginData.is_branch_admin ?? false,
        is_training_enabled: loginData.is_training_enabled ?? false,
        can_create: loginData.can_create ?? false,
        can_edit: loginData.can_edit ?? false,
        can_delete: loginData.can_delete ?? false,
        branch_id: loginData.branch_id ?? null,
        branch_name: loginData.branch_name ?? "",
        branch_avail: loginData.branch_avail ?? false,
        multi_branch: loginData.multi_branch ?? false,
        org_admin_branch_selection: loginData.org_admin_branch_selection ?? true,
      };

      // Always save in sessionStorage
      setStorage(sessionStorage, commonData);

      const orgBranchSelection = loginData.org_admin_branch_selection !== false;
      const mergedConfig = {
        ...JSON.parse(sessionStorage.getItem("customConfig") || "{}"),
        org_admin_branch_selection: orgBranchSelection,
      };
      sessionStorage.setItem("customConfig", JSON.stringify(mergedConfig));
      if (keepSignedIn) {
        localStorage.setItem("customConfig", JSON.stringify(mergedConfig));
      }

      if (!loginData.branch_avail) {
        clearActiveBranch();
      }
      enforceOrgAdminBranchPolicy();

      // Optionally save in localStorage
      if (keepSignedIn) {
        const now = new Date();
        setStorage(localStorage, {
          ...commonData,
          keepSignedIn: true,
          keepSignedInExpiry: now.getTime() + 720 * 60 * 1000, // 12 hours
        });
      }

      setIsAuthenticated(true);
      bumpAuthRevision();
    } catch (error) {
      console.error("Error during login:", error);
    }
  }, [bumpAuthRevision]);

  /** --------------------------
   *  Logout
   *  ------------------------- */
  const logoutauth = useCallback(() => {
    removeStorage(sessionStorage);
    removeStorage(localStorage);
    localStorage.removeItem("keepSignedIn");
    localStorage.removeItem("keepSignedInExpiry");
    localStorage.removeItem('sidebar_preference');
    // Clear the org admin's active-branch selection so it doesn't leak into
    // the next login session.
    sessionStorage.removeItem("active_branch_id");
    sessionStorage.removeItem("active_branch_name");
    setIsAuthenticated(false);
    bumpAuthRevision();
  }, [bumpAuthRevision]);

  /** --------------------------
   *  Sync sessionStorage with localStorage
   *  ------------------------- */
  const setSessionStorage = () => {
    const syncedData = {};
    storageKeys.forEach((key) => {
      const value = localStorage.getItem(key);
      if (value !== null) syncedData[key] = value;
    });
    setStorage(sessionStorage, syncedData);
    enforceOrgAdminBranchPolicy();
  };

  /** --------------------------
   *  Expiry checker
   *  ------------------------- */
  const getWithExpiry = () => {
    const expiry = localStorage.getItem("keepSignedInExpiry");
    if (!expiry) return false;
    return new Date().getTime() <= Number(expiry);
  };

  /** --------------------------
   *  Check Authentication
   *  ------------------------- */
  const checkAuth = useCallback(() => {
    if (localStorage.getItem("keepSignedIn")) {
      if (getWithExpiry()) {
        setSessionStorage();
        if (sessionStorage.getItem("token")) {
          setIsAuthenticated(true);
          bumpAuthRevision();
        }
      } else {
        logoutauth();
      }
    } else if (sessionStorage.getItem("token")) {
      setIsAuthenticated(true);
      bumpAuthRevision();
    }
  }, [bumpAuthRevision, logoutauth]);

  /** --------------------------
   *  Helpers exposed
   *  ------------------------- */
  const loginServiceType = useCallback(
    () => sessionStorage.getItem("is_admin"),
    []
  );

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const contextValue = useMemo(
    () => ({
      isAuthenticated,
      authRevision,
      loginauth,
      logoutauth,
      checkAuth,
      loginServiceType,
      refreshSession: refreshAuthSession,
      bumpAuthRevision,
    }),
    [isAuthenticated, authRevision, loginauth, logoutauth, checkAuth, loginServiceType, bumpAuthRevision]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
